import { useTranslation } from "react-i18next";

import { Icon, IndeterminateProgressBar } from "@nexus/ui";

import type { PartnerPreflightUploadPhase } from "@/features/marketplace/api/partner-preflight-upload.api";
import { UploadProgressBar } from "@/features/preflight/components/UploadProgressBar";

export type PartnerPreflightSubmitPhase = PartnerPreflightUploadPhase | "submitting";

interface PartnerPreflightUploadProgressProps {
  phase: Exclude<PartnerPreflightSubmitPhase, "idle">;
  progress: number;
  fileName?: string | null;
}

export function PartnerPreflightUploadProgress({
  phase,
  progress,
  fileName,
}: PartnerPreflightUploadProgressProps) {
  const { t } = useTranslation();
  const showDeterminate = phase === "uploading" && progress > 0 && progress < 100;

  const label =
    phase === "preparing"
      ? t("marketplace.partner.preflightUploadPreparing", "Preparing upload…")
      : phase === "uploading" && progress > 0
        ? t("marketplace.partner.preflightUploadUploadingPercent", {
            percent: Math.round(progress),
            defaultValue: "Uploading video — {{percent}}%",
          })
        : phase === "uploading"
          ? t("marketplace.partner.preflightUploadUploading", "Uploading video…")
          : phase === "confirming"
            ? t("marketplace.partner.preflightUploadConfirming", "Finishing upload…")
            : t("marketplace.partner.preflightUploadSubmitting", "Creating review…");

  return (
    <div
      className="border-border bg-card flex flex-col gap-2 rounded-md border px-3 py-2.5"
      role="status"
      aria-live="polite"
    >
      {fileName ? (
        <p className="truncate text-sm font-medium text-foreground" title={fileName}>
          {fileName}
        </p>
      ) : null}
      <div className="flex items-center gap-2">
        <Icon
          name="loader"
          size="xs"
          className="text-primary shrink-0 animate-spin"
          aria-hidden
        />
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
      {showDeterminate ? (
        <UploadProgressBar percent={progress} />
      ) : (
        <IndeterminateProgressBar />
      )}
    </div>
  );
}
